/**
 * @file 채널 D-day 계산 — 헤더 칩 · 기간 표시줄 · 사이드바가 <b>같은 값</b>을 쓰게 하는 단일 출처.
 *
 * 왜 파일로 뺐는가
 * ----------------
 * 같은 채널을 두고 헤더 칩은 `D-64`, 바로 아래 기간줄은 `D-65 (65일 남음)` 를 동시에 띄우고 있었다.
 * 원인이 두 가지 겹쳐 있었다.
 *
 *  1. 기준 시각이 달랐다 — 칩은 `new Date()`(지금 이 순간), 기간줄·사이드바는 `setHours(0,0,0,0)`(오늘 자정).
 *  2. `new Date("2026-09-30")` 은 <b>UTC 자정</b>으로 파싱된다. KST 로는 그날 09:00 이라
 *     로컬 기준과 늘 +9시간 어긋나고, 그 9시간이 `Math.ceil` 에서 하루로 올림된다.
 *
 * 결과적으로 자정 기준 계산은 <b>항상 하루 많았고</b>, 칩은 오전 9시 이전이면 하루 많아졌다 —
 * 즉 같은 화면이 아침·저녁에 다른 숫자를 보여줬다.
 *
 * 그래서 여기서 두 가지를 고정한다.
 *  - 날짜 문자열은 `new Date(y, m-1, d)` 로 <b>로컬</b> 파싱한다(UTC 해석 금지).
 *  - 양쪽 다 자정으로 맞춘 뒤 날짜 차이만 센다. 시각이 끼어들 여지를 없앤다.
 */

/** "2026-09-30" / Date / null → 로컬 자정 Date. 파싱 불가면 null. */
export function toLocalDate(value) {
    if (!value) return null;
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** 오늘(로컬 자정). */
export function today() {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

/**
 * 오늘부터 target 까지 남은 <b>일수</b>. 오늘이면 0, 지났으면 음수.
 * 양쪽 다 자정이라 시각에 따라 값이 흔들리지 않는다.
 */
export function daysUntil(target) {
    const d = toLocalDate(target);
    if (!d) return null;
    return Math.round((d.getTime() - today().getTime()) / 86400000);
}

/** 마감일 라벨 — "D-64" / "D-Day" / "D+3". 마감일이 없으면 null. */
export function ddayLabel(deadline, { expired = false } = {}) {
    const diff = daysUntil(deadline);
    if (diff === null) return null;
    if (expired || diff < 0) return expired && diff >= 0 ? "마감" : `D+${Math.abs(diff)}`;
    return diff === 0 ? "D-Day" : `D-${diff}`;
}

/** fmt — "2026.09.30". 로컬 파싱이라 하루 밀리지 않는다. */
export function fmtDate(value) {
    const d = toLocalDate(value);
    if (!d) return "";
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}
